import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import {
  Card, PageHeader, Table, Btn, Badge,
  StatusBadge, SearchInput, FilterBar, FilterSelect, StatsGrid, StatCard,
} from '../components/UI';
import { IconUsers, IconArrow } from '../components/Icons';

const IconWallet = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 4H3a2 2 0 00-2 2v12a2 2 0 002 2h18a2 2 0 002-2V6a2 2 0 00-2-2z" />
    <path d="M1 10h22" />
  </svg>
);

const IconSub = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
  </svg>
);

const fmt = {
  naira: (n) => '₦' + parseFloat(n || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  date:  (d) => new Date(d).toLocaleDateString('en-NG', { day: '2-digit', month: 'short', year: 'numeric' }),
};

export default function LodgeDetail() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const toast        = useToast();

  const [lodge,   setLodge]   = useState(null);
  const [users,   setUsers]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [status,  setStatus]  = useState('all');

  const load = () => {
    setLoading(true);
    api.getLodgeDetail(id, { page, search, status })
      .then(r => {
        setLodge(r.lodge);
        setUsers(r.users);
        setTotal(r.total);
      })
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id, page, search, status]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [search, status]);

  const activeUsers  = users.filter(u =>  u.is_active).length;
  const inactiveUsers = users.filter(u => !u.is_active).length;

  const mobileCard = (u) => (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{
          width: 34, height: 34, borderRadius: '50%',
          background: 'var(--accent-dim)', color: 'var(--accent)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 700, flexShrink: 0,
        }}>
          {u.name?.[0]?.toUpperCase()}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
        </div>
        <StatusBadge status={u.is_active ? 'active' : 'inactive'} />
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', fontSize: 11, color: 'var(--text3)' }}>
        <span>Balance: <b style={{ color: 'var(--green)' }}>{fmt.naira(u.wallet?.balance)}</b></span>
        <span>·</span>
        <span>Joined: {fmt.date(u.createdAt)}</span>
      </div>
      <div style={{ marginTop: 8 }}>
        <Btn size="xs" variant="ghost" onClick={() => navigate(`/users/${u.id}`)}>
          View →
        </Btn>
      </div>
    </>
  );

  if (!loading && !lodge) {
    return (
      <div style={{ textAlign: 'center', padding: 80, color: 'var(--text3)' }}>
        Lodge not found.{' '}
        <button onClick={() => navigate('/lodges')}
          style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13 }}>
          ← Back to Lodges
        </button>
      </div>
    );
  }

  return (
    <div className="page-enter">
      <PageHeader
        title={lodge?.name ?? '…'}
        subtitle={`${total} user${total !== 1 ? 's' : ''} in this lodge`}
        action={
          <Btn variant="ghost" onClick={() => navigate('/lodges')}>
            ← Back to Lodges
          </Btn>
        }
      />

      {/* Stats */}
      <StatsGrid>
        <StatCard
          label="Total Users"
          value={total}
          sub="Registered in lodge"
          icon={IconUsers}
          accent="var(--accent)"
        />
        <StatCard
          label="Active"
          value={lodge ? users.filter(u => u.is_active).length : '—'}
          sub="Accounts active"
          icon={IconUsers}
          accent="var(--green)"
        />
        <StatCard
          label="Inactive"
          value={lodge ? users.filter(u => !u.is_active).length : '—'}
          sub="Accounts suspended"
          icon={IconUsers}
          accent="var(--red)"
        />
        <StatCard
          label="Lodge Status"
          value={lodge?.is_active ? 'Active' : 'Inactive'}
          sub={lodge?.is_active ? 'Accepting signups' : 'Not accepting signups'}
          icon={IconSub}
          accent={lodge?.is_active ? 'var(--green)' : 'var(--text3)'}
        />
      </StatsGrid>

      {/* Filters */}
      <FilterBar>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by name or email…"
        />
        <FilterSelect
          value={status}
          onChange={setStatus}
          options={[
            { value: 'all',      label: 'All Users'  },
            { value: 'active',   label: 'Active'     },
            { value: 'inactive', label: 'Inactive'   },
          ]}
        />
        {(search || status !== 'all') && (
          <Btn size="sm" variant="ghost" onClick={() => { setSearch(''); setStatus('all'); }}>
            Clear ✕
          </Btn>
        )}
      </FilterBar>

      {/* Table */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <Table
          loading={loading}
          rows={users}
          mobileCard={mobileCard}
          emptyText={search ? `No users match "${search}".` : 'No users in this lodge yet.'}
          onRowClick={u => navigate(`/users/${u.id}`)}
          columns={[
            {
              key: 'name', label: 'User',
              render: u => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: '50%',
                    background: 'var(--accent-dim)', color: 'var(--accent)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700, flexShrink: 0,
                  }}>
                    {u.name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{u.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{u.email}</div>
                  </div>
                </div>
              ),
            },
            {
              key: 'balance', label: 'Balance',
              render: u => (
                <span style={{ fontFamily: 'Geist Mono, monospace', fontWeight: 700, color: 'var(--green)', fontSize: 13 }}>
                  {fmt.naira(u.wallet?.balance)}
                </span>
              ),
            },
            {
              key: 'status', label: 'Status',
              render: u => <StatusBadge status={u.is_active ? 'active' : 'inactive'} />,
            },
            {
              key: 'verified', label: 'Verified',
              render: u => <Badge color={u.is_verified ? 'green' : 'gray'}>{u.is_verified ? 'Yes' : 'No'}</Badge>,
            },
            {
              key: 'joined', label: 'Joined',
              render: u => <span style={{ color: 'var(--text3)', fontSize: 12 }}>{fmt.date(u.createdAt)}</span>,
            },
            {
              key: 'actions', label: '',
              render: u => (
                <Btn size="xs" variant="ghost" icon={IconArrow}
                  onClick={() => navigate(`/users/${u.id}`)}>
                  View
                </Btn>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}